import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { confirmAlert } from "react-confirm-alert";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { findByAtividade, remove, save } from "./api-atividade-aluno";
import AtividadeProfile from "../Atividade/AtividadeProfile";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import Loading from "../UI/Loading";
import AtividadeAlunoForm from "./AtividadeAlunoForm";
import AtividadeAlunoTable from "./AtividadeAlunoTable";

const porNumeroChamada = (a, b) =>
  (a.alunoTurma?.numeroChamada ?? Infinity) - (b.alunoTurma?.numeroChamada ?? Infinity);

export default function AtividadeAlunoContainer() {
  const atividade = useSelector((state) => state.atividade);
  const [atividadeAlunos, setAtividadeAlunos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingCodigo, setSavingCodigo] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const codAtividade = atividade?.codigo;

  useEffect(() => {
    if (!codAtividade) return undefined;
    let active = true;
    setIsLoading(true);
    findByAtividade(codAtividade)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
          setAtividadeAlunos([]);
        } else if (Array.isArray(data)) {
          setAtividadeAlunos([...data].sort(porNumeroChamada));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [codAtividade]);

  const codigosNaAtividade = useMemo(
    () => new Set(atividadeAlunos.map((aa) => aa.alunoTurma?.codigo)),
    [atividadeAlunos],
  );

  const handleSave = (persist) => {
    setSavingCodigo(persist.codigo);
    return save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar.");
          return false;
        }
        setAtividadeAlunos((prevState) => prevState.map((aa) => (aa.codigo === data.codigo ? data : aa)));
        return true;
      })
      .finally(() => setSavingCodigo(null));
  };

  const handleDelete = (aa) => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover ${aa.aluno?.nome ?? "o aluno"} desta atividade?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(aa.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              setAtividadeAlunos((prevState) => prevState.filter((item) => item.codigo !== aa.codigo));
              success("Aluno removido da atividade.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const handleAddAluno = (alunoTurma) => {
    setOpenAdd(false);
    save({ atividade, aluno: alunoTurma.aluno, alunoTurma }).then((data) => {
      if (!data || data.error) {
        errorMessage(data?.message || "Não foi possível adicionar o aluno.");
        return;
      }
      setAtividadeAlunos((prevState) => [...prevState, data].sort(porNumeroChamada));
      success(`${alunoTurma.aluno?.nome ?? "Aluno"} adicionado(a) à atividade.`);
    });
  };

  if (!atividade) {
    return (
      <Container>
        <Stack alignItems="center" spacing={2} py={6}>
          <AssignmentOutlinedIcon fontSize="large" color="disabled" />
          <Typography color="text.secondary">Nenhuma atividade selecionada.</Typography>
          <Button variant="contained" component={Link} to="/atividade">
            Ir para Atividades
          </Button>
        </Stack>
      </Container>
    );
  }

  const concluidos = atividade.semNota
    ? atividadeAlunos.filter((aa) => aa.vistoProfessor).length
    : atividadeAlunos.filter((aa) => aa.nota !== null && aa.nota !== undefined && aa.nota !== "").length;

  const renderConteudo = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando alunos da atividade..." />
        </Box>
      );
    }
    if (atividadeAlunos.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2">Nenhum aluno nesta atividade.</Typography>
        </Box>
      );
    }
    return (
      <>
        <Typography variant="body2" color="text.secondary" mb={1}>
          {concluidos} de {atividadeAlunos.length} aluno(s) {atividade.semNota ? "vistado(s)" : "com nota"}
          {!atividade.semNota && " · Digite a nota e pressione Enter para salvar e ir ao próximo."}
        </Typography>
        <AtividadeAlunoTable
          atividade={atividade}
          atividadeAlunos={atividadeAlunos}
          onSave={handleSave}
          onDelete={handleDelete}
          savingCodigo={savingCodigo}
        />
      </>
    );
  };

  return (
    <Container>
      <AtividadeAlunoForm
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        codTurma={atividade.tdp?.turma?.codigo}
        codigosNaAtividade={codigosNaAtividade}
        onSelect={handleAddAluno}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6">Atividade - Alunos</Typography>
          <Typography variant="body2" color="text.secondary">
            {atividade.semNota ? "Registre o visto de cada aluno." : "Lance as notas dos alunos nesta atividade."}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<PersonAddAltOutlinedIcon />} onClick={() => setOpenAdd(true)}>
          Adicionar Aluno
        </Button>
      </Stack>

      <Stack spacing={3}>
        <AtividadeProfile />
        <Box>{renderConteudo()}</Box>
      </Stack>
    </Container>
  );
}
